import { Mail, Heart } from 'lucide-react';

import { Toaster } from '../components/ui/toaster';
import { useMainPhoto } from '../hooks/useMainPhoto';


// --- Main Homepage Component ---
export default function Home() {
  const { photo, loading, refetch } = useMainPhoto();

  return (
    <div className="bg-white text-slate-800 min-h-screen font-sans">
      {/* Header */}
      <header className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto flex justify-between items-center">
          <h2 className="font-serif text-2xl font-bold text-slate-900">
            The Christopher Wright Foundation
          </h2>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 md:py-20">
        {/* Hero Section */}
        <section className="text-center">
          <p className="text-amber-600 font-semibold tracking-wider">
            LOVE WITHOUT LIMITS
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mt-4 leading-tight max-w-4xl mx-auto">
            Chris's Legacy Lives On
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            In loving memory of Christopher Wright (1984-2023). His warm presence, infectious smile, and boundless kindness continue to inspire us all.
          </p>
        </section>

        {/* Image & Mission Section */}
        <section className="mt-16 md:mt-24">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16">
            <div className="lg:w-1/2 w-full max-w-md">
              {loading ? (
                <div className="rounded-lg w-full aspect-square bg-slate-200 animate-pulse"></div>
              ) : (
                <img
                  alt="A smiling portrait of Christopher Wright"
                  className="rounded-lg shadow-xl w-full h-auto object-cover aspect-square"
                  src={photo?.url || "https://placehold.co/600x600/E2E8F0/475569?text=Loading..."}
                />
              )}
            </div>
            <div className="lg:w-1/2 text-left space-y-6">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900">
                Our Mission
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                To know Chris was to love him. The Christopher Wright Foundation was created to carry that love forward. As a registered 501(c)(3) nonprofit, we are dedicated to supporting our community in his honor.
              </p>
              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 text-amber-700 p-2 rounded-full mt-1">
                    <Heart size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">Support for Families</h3>
                    <p className="text-slate-600">Providing crisis response, trauma counseling, and victim services to those affected by gun violence.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                   <div className="bg-amber-100 text-amber-700 p-2 rounded-full mt-1">
                    <Heart size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">Empowerment & Access</h3>
                    <p className="text-slate-600">Expanding access to health and family services and empowering single mothers with resources for stability.</p>
                  </div>
                </div>
                 <div className="flex items-start gap-4">
                   <div className="bg-amber-100 text-amber-700 p-2 rounded-full mt-1">
                    <Heart size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">Community Events</h3>
                    <p className="text-slate-600">Hosting gatherings that celebrate Chris's life, keep his spirit alive, and bring people together.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="mt-20 md:mt-32 text-center bg-slate-50 py-16 rounded-lg">
           <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900">Honor His Legacy</h2>
            <p className="mt-4 text-lg text-slate-600 max-w-3xl mx-auto">
              Your generosity powers practical help and hopeful community. Every gift makes a difference in the lives of families and neighbors in Chris's name.
            </p>
           <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <stripe-buy-button
              buy-button-id="buy_btn_1S6hpU0NVhsKwFiDImkIehiY"
              publishable-key="pk_live_51Rw4RF0NVhsKwFiDKxmYjPpuqyym2xOIBIAUnlXtFiQoeTyj9T6U7yfprPNkDuM0h19KEgkfqvRDyi3pWaA6yFZb007FgMlYLx"
            >
            </stripe-buy-button>
             <a
              className="inline-flex items-center justify-center gap-2 text-slate-700 font-bold py-4 px-10 rounded-full hover:bg-slate-200 transition-colors duration-300"
              href="mailto:contact@christopherwrightfoundation.org"
            >
              <Mail size={20} />
              Get Involved
            </a>
           </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white mt-16 py-8 border-t border-slate-100">
        <div className="container mx-auto text-center text-slate-500 text-sm">
          <p>© 2025 The Christopher Wright Foundation. All Rights Reserved.</p>
          <p className="mt-1">A Non-Profit Organization</p>
        </div>
      </footer>

      {/* Modals and Toasters */}
      <Toaster />
    </div>
  );
}
